<template>
  <div class="floor-plan-container">
    <div v-if="!venue || !venue.imageUrl" class="no-floor-plan">
      <p>{{ $t('tsFloorPlanUnavailable') }}</p>
    </div>
    <div v-else>
      <div class="floor-plan-header">
        <h3 class="floor-plan-title">{{ $t('tsSelectSeatTitle') }}<span v-if="subscriberName"> · {{ subscriberName }}</span></h3>
        <div class="zoom-controls">
          <v-btn icon @click="zoomIn">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn icon @click="zoomOut">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
        </div>
      </div>
      <div ref="svgContainer" class="svg-container">
        <object id="svgfile" :data="venue.imageUrl" type="image/svg+xml" @load="onSvgLoad" />
      </div>
      <div class="legend">
        <div v-if="singleSelection" class="legend-item"><div class="legend-color in-cart" /><span>{{ $t('tsSeatInCart') }}</span></div>
        <div class="legend-item">
          <div class="legend-color available" />
          <span>{{ $t('tsSeatAvailable') }}</span>
        </div>
        <div class="legend-item">
          <div class="legend-color sold" />
          <span>{{ $t('tsSeatSold') }}</span>
        </div>
        <div class="legend-item">
          <div class="legend-color selected" />
          <span>{{ $t('tsSeatSelected') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as svgPanZoom from 'svg-pan-zoom'
import * as Hammer from 'hammerjs'

export default {
  name: 'floor-plan',
  props: {
    subscriberName: { type: String, default: '' },
    singleSelection: { type: Boolean, default: false },
    blockedSeatIds: { type: Array, default: () => [] },
    venue: {
      type: Object,
      default: null,
    },
    venueAvailability: {
      type: Object,
      default: () => ({}),
    },
    initialSelectedSeatId: {
      type: String,
      default: null,
    },
    initialSelectedSeatIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedSeatIds: [],
      panZoomInstance: null,
      hammerInstance: null,
      svgDocument: null,
      touchMoveHandler: null,
      touchMoveElement: null,
      seatListeners: [],
      lastGestureWasDrag: false,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
    }
  },
  watch: {
    blockedSeatIds() { if (this.svgDocument) this.initialSvgColors(this.svgDocument) },
    venue: {
      handler() {
        this.selectedSeatIds = []
        this.$emit('seat-selected', [])
        this.destroyInteractions()
      },
    },
    venueAvailability: {
      handler() {
        if (this.svgDocument) {
          this.initialSvgColors(this.svgDocument)
        }
      },
      deep: true,
    },
    initialSelectedSeatId: {
      handler() {
        if (this.svgDocument) {
          this.initialSvgColors(this.svgDocument)
          this.applyInitialSelection()
        }
      },
    },
    initialSelectedSeatIds: {
      handler() {
        if (this.svgDocument) {
          this.selectedSeatIds = []
          this.initialSvgColors(this.svgDocument)
          this.applyInitialSelections()
        }
      },
    },
  },
  beforeDestroy() {
    this.destroyInteractions()
  },
  methods: {
    isSeatAvailableState(state) {
      return state === 'available' || (state && state.state === 'available')
    },
    isSeatSelectable(seatId, state) {
      return !this.blockedSeatIds.includes(seatId) && this.isSeatAvailableState(state)
    },
    onSvgLoad(event) {
      const svgObject = event.target
      const svgDoc = svgObject && svgObject.contentDocument

      if (!svgDoc) return

      const svgElement = svgDoc.documentElement
      if (!svgElement) return

      this.destroyInteractions()

      this.svgDocument = svgDoc

      const options = {
        zoomEnabled: true,
        controlIconsEnabled: false,
        fit: true,
        center: true,
        minZoom: 0.8,
        maxZoom: this.isMobile ? 5 : 3,
      }

      if (this.isMobile) {
        options.customEventsHandler = this.createMobileEventsHandler()
      }

      try {
        this.panZoomInstance = svgPanZoom(svgElement, options)
      } catch (error) {
        console.warn('Failed to initialize floor plan pan/zoom:', error)

        return
      }

      this.initialSvgColors(svgDoc)
      this.applyInitialSelection()
      this.applyInitialSelections()
    },
    applyInitialSelection() {
      if (!this.svgDocument) return
      if (!this.initialSelectedSeatId) return

      const state = this.venueAvailability[this.initialSelectedSeatId]
      if (!this.isSeatSelectable(this.initialSelectedSeatId, state)) return

      const seatElement = this.svgDocument.getElementById(this.initialSelectedSeatId)
      if (!seatElement) return

      this.setSeatSelected(this.initialSelectedSeatId, true)
    },
    applyInitialSelections() {
      if (!this.svgDocument) return
      if (!Array.isArray(this.initialSelectedSeatIds) || !this.initialSelectedSeatIds.length) return

      this.initialSelectedSeatIds.forEach(seatId => {
        const state = this.venueAvailability[seatId]
        if (!this.isSeatSelectable(seatId, state)) return
        const seatElement = this.svgDocument.getElementById(seatId)
        if (!seatElement) return
        this.setSeatSelected(seatId, true)
      })
    },
    initialSvgColors(svgDoc) {
      if (!svgDoc) return

      this.removeSeatListeners()

      const seatElements = svgDoc.querySelectorAll('[id*="Seat-"][id*="Row-"]')
      const availableSeatIds = []
      let hasNonAvailable = false

      seatElements.forEach(seatElement => {
        const seatId = seatElement.id
        const seatState = this.venueAvailability[seatId]

        let color = '#BDBDBD'
        const isAvailable = this.isSeatAvailableState(seatState)
        const isSold = seatState === 'sold' || seatState === 'hold'
        const isSelectable = this.isSeatSelectable(seatId, seatState)

        if (this.blockedSeatIds.includes(seatId)) {
          color = '#BDBDBD'
        } else if (isAvailable) {
          color = '#4CAF50'
          availableSeatIds.push(seatId)
        } else if (isSelectable) {
          color = '#4CAF50'
        } else if (isSold) {
          color = '#F44336'
          hasNonAvailable = true
        } else if (seatState) {
          hasNonAvailable = true
        }

        seatElement.style.fill = this.selectedSeatIds.includes(seatId) && isSelectable ? '#2196F3' : color
        seatElement.style.stroke = ''
        seatElement.style.strokeWidth = ''

        if (isSelectable) {
          seatElement.style.cursor = 'pointer'
          seatElement.style.pointerEvents = 'all'
          const listener = event => {
            if (event && typeof event.preventDefault === 'function') {
              event.preventDefault()
            }
            if (event && typeof event.stopPropagation === 'function') {
              event.stopPropagation()
            }
            if (this.isMobile && this.shouldIgnoreSeatSelection(event)) {
              return
            }
            this.handleSeatClick(seatId)
            if (this.isMobile) {
              this.resetGestureTracking()
            }
          }

          const events = ['click']

          events.forEach(type => {
            const options = type === 'touchend' ? { passive: false } : false
            seatElement.addEventListener(type, listener, options)
          })

          this.seatListeners.push({ element: seatElement, listener, events })
        } else {
          seatElement.style.cursor = 'not-allowed'
          seatElement.style.pointerEvents = 'none'
        }
      })

      if (this.selectedSeatIds.length) {
        this.selectedSeatIds = this.selectedSeatIds.filter(seatId => {
          const state = this.venueAvailability[seatId]
          const isAvailable = this.isSeatSelectable(seatId, state)
          const selectedElement = svgDoc.getElementById(seatId)
          if (isAvailable && selectedElement) {
            selectedElement.style.fill = '#2196F3'
            selectedElement.style.stroke = '#1565C0'
            selectedElement.style.strokeWidth = '2px'

            return true
          }

          return false
        })
        this.$emit('seat-selected', this.selectedSeatIds)
      } else if (!this.singleSelection && availableSeatIds.length === 1 && hasNonAvailable) {
        // Auto-select the only available seat (useful for pre-order renewals).
        this.handleSeatClick(availableSeatIds[0])
      }
    },
    setSeatSelected(seatId, selected) {
      const hasSeat = this.selectedSeatIds.includes(seatId)
      if (selected && !hasSeat) {
        if (this.singleSelection) {
          this.selectedSeatIds.forEach(id => {
            const previous = this.svgDocument && this.svgDocument.getElementById(id)
            if (previous) { previous.style.fill = '#4CAF50'; previous.style.stroke = ''; previous.style.strokeWidth = '' }
          })
          this.selectedSeatIds = []
        }
        this.selectedSeatIds = [...this.selectedSeatIds, seatId]
      } else if (!selected && hasSeat) {
        this.selectedSeatIds = this.selectedSeatIds.filter(id => id !== seatId)
      }

      if (this.svgDocument) {
        const seatElement = this.svgDocument.getElementById(seatId)
        if (seatElement) {
          seatElement.style.fill = selected ? '#2196F3' : '#4CAF50'
          seatElement.style.stroke = selected ? '#1565C0' : ''
          seatElement.style.strokeWidth = selected ? '2px' : ''
        }
      }

      this.$emit('seat-selected', this.selectedSeatIds)
    },
    handleSeatClick(seatId) {
      const svgDoc = this.svgDocument
      if (!svgDoc) return

      const state = this.venueAvailability[seatId]
      const isAvailable = this.isSeatSelectable(seatId, state)
      if (!isAvailable) {
        return
      }

      const isSelected = this.selectedSeatIds.includes(seatId)
      this.setSeatSelected(seatId, !isSelected)
    },
    zoomIn() {
      if (this.panZoomInstance) {
        this.panZoomInstance.zoomIn()
      }
    },
    zoomOut() {
      if (this.panZoomInstance) {
        this.panZoomInstance.zoomOut()
      }
    },
    createMobileEventsHandler() {
      return {
        haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
        init: options => {
          const instance = options.instance
          let initialScale = 1
          let pannedX = 0
          let pannedY = 0

          this.hammerInstance = Hammer(options.svgElement, {
            inputClass: Hammer.SUPPORT_POINTER_EVENTS
              ? Hammer.PointerEventInput
              : Hammer.TouchInput,
          })

          this.hammerInstance.get('pinch').set({ enable: true })

          this.hammerInstance.on('doubletap', () => instance.zoomIn())

          this.hammerInstance.on('panstart panmove', ev => {
            this.lastGestureWasDrag = true
            if (ev.type === 'panstart') {
              pannedX = 0
              pannedY = 0
            }

            instance.panBy({
              x: ev.deltaX - pannedX,
              y: ev.deltaY - pannedY,
            })

            pannedX = ev.deltaX
            pannedY = ev.deltaY
          })

          this.hammerInstance.on('pinchstart pinchmove', ev => {
            this.lastGestureWasDrag = true
            if (ev.type === 'pinchstart') {
              initialScale = instance.getZoom()
            }

            instance.zoomAtPoint(initialScale * ev.scale, {
              x: ev.center.x,
              y: ev.center.y,
            })
          })

          if (this.touchMoveElement && this.touchMoveHandler) {
            this.touchMoveElement.removeEventListener('touchmove', this.touchMoveHandler)
          }

          this.touchMoveHandler = event => {
            if (event.cancelable) {
              event.preventDefault()
            }
          }
          this.touchMoveElement = options.svgElement
          options.svgElement.addEventListener('touchmove', this.touchMoveHandler, { passive: false })
        },
        destroy: () => {
          if (this.hammerInstance) {
            this.hammerInstance.destroy()
            this.hammerInstance = null
          }

          if (this.touchMoveElement && this.touchMoveHandler) {
            this.touchMoveElement.removeEventListener('touchmove', this.touchMoveHandler)
          }

          this.touchMoveElement = null
          this.touchMoveHandler = null
        },
      }
    },
    shouldIgnoreSeatSelection(event) {
      if (!this.isMobile) return false

      if (this.lastGestureWasDrag) {
        this.lastGestureWasDrag = false

        return true
      }

      return false
    },
    removeSeatListeners() {
      if (!this.seatListeners.length) return

      this.seatListeners.forEach(({ element, listener, events }) => {
        events.forEach(type => {
          const options = type === 'touchend' ? { passive: false } : false
          element.removeEventListener(type, listener, options)
        })
      })

      this.seatListeners = []
    },
    destroyInteractions() {
      if (this.panZoomInstance) {
        this.panZoomInstance.destroy()
        this.panZoomInstance = null
      }

      if (this.hammerInstance) {
        this.hammerInstance.destroy()
        this.hammerInstance = null
      }

      if (this.touchMoveElement && this.touchMoveHandler) {
        this.touchMoveElement.removeEventListener('touchmove', this.touchMoveHandler)
      }

      this.touchMoveElement = null
      this.touchMoveHandler = null
      this.lastGestureWasDrag = false

      this.removeSeatListeners()
      this.svgDocument = null
    },
  },
}
</script>

<style scoped>
.floor-plan-container {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}
.no-floor-plan {
  text-align: center;
  padding: 48px;
  color: #757575;
}
.floor-plan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.floor-plan-title {
  font-size: 1.25rem;
  font-weight: 500;
}
.svg-container {
  width: 100%;
  height: 500px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  touch-action: none;
}
#svgfile {
  width: 100%;
  height: 100%;
}
.legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}
.in-cart { background-color: #bdbdbd; }
.available {
  background-color: #4caf50;
}
.sold {
  background-color: #f44336;
}
.selected {
  background-color: #2196f3;
}
@media (max-width: 600px) {
  .floor-plan-container {
    padding: 12px;
  }
  .floor-plan-header {
    margin-bottom: 12px;
  }
  .legend {
    gap: 16px;
    margin-top: 12px;
  }
}

.legend { flex-wrap: wrap; row-gap: 10px; }
.legend-item { max-width: 100%; }
.legend-item span { overflow-wrap: anywhere; }
</style>
